import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { proposalAPI, pdfAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  MagnifyingGlass,
  ArrowRight,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Trophy,
  FilePdf,
  FloppyDisk,
  Pencil,
  Trash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ProposalsPage = () => {
  const [proposals, setProposals] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchProposals();
    fetchDrafts();
  }, []);

  const fetchProposals = async () => {
    setLoading(true);
    try {
      const response = await proposalAPI.getMine();
      setProposals(response.data);
    } catch (error) {
      console.error('Failed to fetch proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrafts = async () => {
    try {
      const response = await proposalAPI.getDrafts();
      setDrafts(response.data || []);
    } catch (e) { /* ok */ }
  };

  const handleDeleteDraft = async (quotationId) => {
    try {
      await proposalAPI.deleteDraft(quotationId);
      setDrafts(prev => prev.filter(d => d.quotation_id !== quotationId));
      toast.success('Borrador eliminado');
    } catch (e) {
      toast.error('Error al eliminar borrador');
    }
  };

  const getStatusInfo = (proposal) => {
    if (proposal.is_winner) {
      return { label: 'Ganador', className: 'bg-green-100 text-green-700 border-green-300', icon: Trophy };
    }
    const statusMap = {
      draft: { label: 'Borrador', className: 'bg-zinc-100 text-zinc-700 border-zinc-300', icon: FileText },
      sent: { label: 'Enviada', className: 'bg-blue-100 text-blue-700 border-blue-300', icon: Clock },
      selected: { label: 'Seleccionada', className: 'bg-amber-100 text-amber-700 border-amber-300', icon: CheckCircle },
      accepted: { label: 'Aceptada', className: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
      rejected: { label: 'Rechazada', className: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
      not_selected: { label: 'No seleccionada', className: 'bg-zinc-100 text-zinc-500 border-zinc-300', icon: XCircle },
    };
    return statusMap[proposal.status] || { label: proposal.status, className: 'bg-zinc-100 text-zinc-700', icon: FileText };
  };

  const filteredProposals = proposals.filter(p => {
    const matchesSearch = p.quotation_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          p.quotation_category?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'winners') return matchesSearch && p.is_winner;
    if (activeTab === 'pending') return matchesSearch && ['sent', 'selected'].includes(p.status);
    if (activeTab === 'completed') return matchesSearch && ['accepted', 'rejected', 'not_selected'].includes(p.status);
    return matchesSearch;
  });

  const statusCounts = {
    all: proposals.length,
    winners: proposals.filter(p => p.is_winner).length,
    pending: proposals.filter(p => ['sent', 'selected'].includes(p.status)).length,
    completed: proposals.filter(p => ['accepted', 'rejected', 'not_selected'].includes(p.status)).length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 bg-zinc-200 animate-pulse"></div>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-zinc-200 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="proposals-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Mis Propuestas</h1>
        <p className="text-zinc-600">Gestiona todas tus propuestas enviadas</p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
        <Input
          placeholder="Buscar propuestas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-none"
          data-testid="search-proposals"
        />
      </div>

      {/* Drafts Section */}
      {drafts.length > 0 && (
        <Card className="border-2 border-blue-200 dark:border-blue-800 rounded-none bg-blue-50/50 dark:bg-blue-900/10" data-testid="drafts-section">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <FloppyDisk className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-800 dark:text-blue-300">Borradores en Progreso ({drafts.length})</h3>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {drafts.map((draft) => {
                const pct = draft.total_count > 0 ? Math.round((draft.filled_count / draft.total_count) * 100) : 0;
                return (
                  <div key={draft.quotation_id} className="bg-white dark:bg-zinc-800 border border-blue-200 dark:border-blue-800 p-3" data-testid={`draft-card-${draft.quotation_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-zinc-900 dark:text-zinc-100 truncate">{draft.quotation_name}</p>
                        <Badge variant="outline" className="rounded-none text-xs mt-1">{draft.quotation_category}</Badge>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 flex-shrink-0 h-7 w-7 p-0" onClick={() => handleDeleteDraft(draft.quotation_id)}>
                        <Trash className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 bg-zinc-200 dark:bg-zinc-700 h-2 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct > 50 ? 'bg-orange-400' : 'bg-blue-400'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-zinc-600 dark:text-zinc-400 flex-shrink-0">{pct}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-400">{draft.filled_count}/{draft.total_count} materiales</span>
                      <Link to={`/quotations/${draft.quotation_id}`}>
                        <Button size="sm" className="rounded-none h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold" data-testid={`continue-draft-${draft.quotation_id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Continuar
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto max-w-full">
        <TabsList className="bg-zinc-100 rounded-none p-1 border border-zinc-200 w-max">
          <TabsTrigger value="all" className="rounded-none data-[state=active]:bg-white">
            Todas ({statusCounts.all})
          </TabsTrigger>
          <TabsTrigger value="winners" className="rounded-none data-[state=active]:bg-white">
            Ganadas ({statusCounts.winners})
          </TabsTrigger>
          <TabsTrigger value="pending" className="rounded-none data-[state=active]:bg-white">
            Pendientes ({statusCounts.pending})
          </TabsTrigger>
          <TabsTrigger value="completed" className="rounded-none data-[state=active]:bg-white">
            Finalizadas ({statusCounts.completed})
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value={activeTab} className="mt-6">
          {filteredProposals.length === 0 ? (
            <Card className="border-zinc-200 rounded-none">
              <CardContent className="p-12 text-center">
                <FileText className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">
                  No hay propuestas
                </h3>
                <p className="text-zinc-500 mb-6">
                  Explora las cotizaciones disponibles para enviar propuestas
                </p>
                <Link to="/quotations">
                  <Button className="btn-primary rounded-none">
                    Ver Cotizaciones
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredProposals.map((proposal) => {
                const statusInfo = getStatusInfo(proposal);
                return (
                  <Link 
                    key={proposal.proposal_id} 
                    to={`/proposals/${proposal.proposal_id}`}
                    data-testid={`proposal-card-${proposal.proposal_id}`}
                  >
                    <Card className="border-zinc-200 rounded-none hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] transition-shadow cursor-pointer">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-zinc-900">{proposal.quotation_name}</h3>
                              <Badge variant="outline" className={`rounded-none ${statusInfo.className}`}>
                                <statusInfo.icon className="w-3 h-3 mr-1" />
                                {statusInfo.label}
                              </Badge>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-600">
                              <Badge variant="outline" className="rounded-none">
                                {proposal.quotation_category}
                              </Badge>
                              {proposal.project_name && (
                                <span>Proyecto: {proposal.project_name}</span>
                              )}
                              <span>
                                {new Date(proposal.created_at).toLocaleDateString('es-MX')}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-zinc-500">Total</p>
                              <p className="text-xl font-bold text-orange-600">
                                ${proposal.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                              </p>
                              <p className="text-xs text-zinc-500">MXN</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="rounded-none text-orange-600"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                (async () => {
                                  try {
                                    const response = await pdfAPI.downloadProposalPDF(proposal.proposal_id);
                                    const url = URL.createObjectURL(response.data);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `propuesta_${proposal.proposal_id.slice(0,12)}.pdf`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    toast.success('PDF descargado');
                                  } catch (err) {
                                    toast.error('Error al descargar PDF');
                                  }
                                })();
                              }}
                              data-testid={`download-pdf-${proposal.proposal_id}`}
                            >
                              <FilePdf className="w-5 h-5" />
                            </Button>
                            <Button variant="ghost" className="rounded-none">
                              <ArrowRight className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProposalsPage;
